import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Switch, Typography } from '@mui/material';
import { Keyboard as KeyboardIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { useOnline } from '../hooks/useOnline';
import { browserSettingsService } from '../services/browserSettingsService';
import { BrowserSettings, DEFAULT_BROWSER_SETTINGS } from '../types/browserSettings';

/**
 * Websites tab card: may the child type web addresses in the tablet's Browser? Off hides
 * the URL field, Go, Clear and the history there. Backed by
 * devices/{id}/config/browserSettings (contracts/firebase-schema.md §2.6).
 * States follow docs/design/BA-21/design.md §2.3.
 */
const BrowserSettingsSection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();
  const online = useOnline();

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BrowserSettings>(DEFAULT_BROWSER_SETTINGS);

  const isViewer = selectedDevice?.role === 'viewer';

  const loadSettings = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setLoading(true);
      setLoadFailed(false);
      setSettings(await browserSettingsService.getSettings(selectedDevice.id));
    } catch (error) {
      console.error('Failed to load browser settings:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Optimistic toggle with a partial PUT; back to the previous value on error
  const handleToggle = useCallback(
    async (allowUrlEntry: boolean) => {
      if (!selectedDevice?.id || isViewer) return;

      const previous = settings;
      setSettings((prev) => ({ ...prev, allowUrlEntry }));

      try {
        setSaving(true);
        setSettings(await browserSettingsService.updateSettings(selectedDevice.id, { allowUrlEntry }));
        showSnackbar(t('browserSettings.saved'), 'success');
      } catch (error) {
        console.error('Failed to save browser settings:', error);
        setSettings(previous);
        showSnackbar(t('browserSettings.failedToSave'), 'error');
      } finally {
        setSaving(false);
      }
    },
    [selectedDevice?.id, isViewer, settings, showSnackbar, t]
  );

  if (!selectedDevice) return null;

  const name = t('browserSettings.allowUrlEntry.name');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('browserSettings.title')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : loadFailed ? (
          <Box sx={{ py: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('browserSettings.failedToLoad')}
            </Typography>
            <Button variant="outlined" onClick={loadSettings}>
              {t('browserSettings.retry')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
              <KeyboardIcon color={settings.allowUrlEntry ? 'primary' : 'disabled'} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1">{name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('browserSettings.allowUrlEntry.help')}
                </Typography>
              </Box>
              <Switch
                checked={settings.allowUrlEntry}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={isViewer || saving || !online}
                slotProps={{ input: { role: 'switch', 'aria-label': name } }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              {online ? t('browserSettings.liveHint') : t('browserSettings.offline')}
            </Typography>

            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.saving')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BrowserSettingsSection;
